import { useEffect, useState } from 'react';
import { Flame, Trophy, Target, Check } from 'lucide-react';
import { authFetch } from '../lib/authFetch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AMBER = '#E8A020';

// Map an activity count to an amber intensity (GitHub-contributions style).
function levelStyle(count) {
  if (!count) return { backgroundColor: 'rgba(255,255,255,0.04)' };
  if (count >= 6) return { backgroundColor: AMBER };
  if (count >= 3) return { backgroundColor: 'rgba(232,160,32,0.66)' };
  if (count >= 2) return { backgroundColor: 'rgba(232,160,32,0.42)' };
  return { backgroundColor: 'rgba(232,160,32,0.22)' };
}

// Group a flat day list into weeks (columns), padded so each column is 7 days.
function toWeeks(calendar) {
  if (!calendar.length) return [];
  const days = [...calendar];
  // Pad the front so the first column starts on Sunday (JS getDay: 0=Sun).
  const firstDow = new Date(days[0].date + 'T00:00:00Z').getUTCDay();
  for (let i = 0; i < firstDow; i++) days.unshift({ date: null, count: 0 });
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));
  return weeks;
}

export default function WritingStreak() {
  const [data, setData] = useState(null);
  const [savingGoal, setSavingGoal] = useState(false);

  const load = async () => {
    try {
      const res = await authFetch(`${API}/activity/streak`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch { /* non-blocking widget */ }
  };

  useEffect(() => { load(); }, []);

  const setGoal = async (goal) => {
    setSavingGoal(true);
    try {
      const res = await authFetch(`${API}/activity/goal`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ weekly_goal: goal }),
      });
      if (res.ok) setData(d => ({ ...d, weekly_goal: goal }));
    } catch { /* ignore */ } finally { setSavingGoal(false); }
  };

  if (!data) return null;

  const weeks = toWeeks(data.calendar || []);
  const goal = data.weekly_goal || 0;
  const goalMet = goal > 0 && data.week_active_days >= goal;

  return (
    <div className="bg-card border border-border/40 p-6 mb-10">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        {/* Stat counters */}
        <div className="flex items-center gap-8">
          <div className="flex items-center gap-3">
            <Flame className="w-6 h-6" style={{ color: data.current_streak > 0 ? AMBER : undefined }}
                    color={data.current_streak > 0 ? AMBER : 'currentColor'} />
            <div>
              <p className="font-mono text-2xl font-semibold leading-none" style={{ color: AMBER }}>
                {data.current_streak}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                Day Streak
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Trophy className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="font-mono text-2xl font-semibold leading-none">{data.longest_streak}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                Longest
              </p>
            </div>
          </div>
          <div className="hidden sm:block">
            <p className="font-mono text-2xl font-semibold leading-none">{data.total_active_days}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
              {data.total_active_days === 1 ? 'Active Day' : 'Active Days'}
            </p>
          </div>
        </div>

        {/* Heatmap */}
        <div className="overflow-x-auto">
          <div className="flex gap-[3px]">
            {weeks.map((week, wi) => (
              <div key={wi} className="flex flex-col gap-[3px]">
                {week.map((day, di) => (
                  <div
                    key={di}
                    title={day.date ? `${day.date} — ${day.count} action${day.count === 1 ? '' : 's'}` : ''}
                    className="w-[11px] h-[11px]"
                    style={day.date ? levelStyle(day.count) : { backgroundColor: 'transparent' }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Weekly goal */}
      <div className="flex items-center gap-3 mt-5 pt-5 border-t border-border/40">
        <Target className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        {goal > 0 ? (
          <p className="font-mono text-xs text-muted-foreground">
            Weekly goal:{' '}
            <span className="text-foreground">{data.week_active_days}/{goal} days</span>
            {goalMet && (
              <span className="ml-2 inline-flex items-center gap-1" style={{ color: AMBER }}>
                <Check className="w-3 h-3" /> met
              </span>
            )}
          </p>
        ) : (
          <p className="font-mono text-xs text-muted-foreground">Set a weekly writing goal:</p>
        )}
        <div className="flex items-center gap-1 ml-auto">
          {[3, 5, 7].map(n => (
            <button
              key={n}
              type="button"
              disabled={savingGoal}
              onClick={() => setGoal(goal === n ? 0 : n)}
              className={`font-mono text-[11px] px-2.5 py-1 border transition-colors ${
                goal === n
                  ? 'border-primary text-primary bg-primary/10'
                  : 'border-border/40 text-muted-foreground hover:border-primary/60'
              }`}
            >
              {n}×/wk
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
